import { lazy, Suspense, useState, useEffect } from 'react'
import { Routes, Route } from 'react-router-dom'
import { ArrowUp } from 'lucide-react'
import { useScrollProgress } from './hooks/useParallax.js'
import { useScrollToTop } from './hooks/useScrollToTop.js'
import ErrorBoundary from './components/ErrorBoundary.jsx'
import PageLoader from './components/PageLoader.jsx'
import CookieConsent from './components/CookieConsent.jsx'

// ── Eagerly loaded (needed on first paint) ──────────────────────
import Navbar from './components/layout/Navbar.jsx'
import Footer from './components/layout/Footer.jsx'
import Hero from './components/sections/Hero.jsx'
import Problem from './components/sections/Problem.jsx'
import HowItWorks from './components/sections/HowItWorks.jsx'
import ForMerchants from './components/sections/ForMerchants.jsx'
import MerchantForm from './components/sections/MerchantForm.jsx'
import DownloadApp from './components/sections/DownloadApp.jsx'
import Waitlist from './components/sections/Waitlist.jsx'
import About from './components/sections/About.jsx'

// ── Lazy loaded (only fetched when navigated to) ────────────────
const Investors = lazy(() => import('./pages/Investors.jsx'))
const Terms = lazy(() => import('./pages/Terms.jsx'))
const MerchantTerms = lazy(() => import('./pages/MerchantTerms.jsx'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy.jsx'))
const CookiePolicy = lazy(() => import('./pages/CookiePolicy.jsx'))
const AcceptableUse = lazy(() => import('./pages/AcceptableUse.jsx'))
const DisputeResolution = lazy(() => import('./pages/DisputeResolution.jsx'))
const NotFound = lazy(() => import('./pages/NotFound.jsx'))

function MainSite() {
  const progress = useScrollProgress()

  return (
    <>
      <div
        aria-hidden="true"
        className="scroll-progress"
        style={{ transform: `scaleX(${progress})` }}
      />
      <Navbar />
      <main>
        <Hero />
        <Problem />
        <HowItWorks />
        <ForMerchants />
        {/* <MerchantForm /> */}
        <DownloadApp />
        <Waitlist />
        <About />
      </main>
      <Footer />
    </>
  )
}

function ScrollToTop() {
  const [show, setShow] = useState(false)

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 300)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  if (!show) return null

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Scroll to top"
      className="fixed bottom-6 right-6 z-[60] w-10 h-10 rounded-full
                 bg-[#1F2933] text-white shadow-lg
                 flex items-center justify-center
                 hover:bg-primary transition-colors duration-200"
    >
      <ArrowUp className="w-4 h-4" />
    </button>
  )
}

function AppRoutes() {
  useScrollToTop()

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<MainSite />} />
        <Route path="/investors" element={<Investors />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/merchant-terms" element={<MerchantTerms />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/acceptable-use" element={<AcceptableUse />} />
        <Route path="/dispute-resolution" element={<DisputeResolution />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppRoutes />
      <ScrollToTop />
      <CookieConsent />
    </ErrorBoundary>
  )
}
